import { useState, useRef } from 'react'
import { Head, router } from '@inertiajs/react'
import { Table, TableHeader, TableBody, TableRow, TableHead } from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '@/components/ui/alert-dialog'
import { Search } from 'lucide-react'
import WorkerPortalShell from '@/frontend/layouts/worker-first/WorkerPortalShell'
import { usePermissions } from '@/frontend/hooks/usePermissions'
import type { ScopeWorker } from '@/frontend/types/inertia'
import type { DigitalWorkerDetail } from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import ArtifactRow, { type Artifact } from './ArtifactRow'
import { EmptyState, FilterEmptyState } from '@/frontend/components/EmptyState'

interface Pagination {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface CurrentFilters {
  search: string
  status: string
  kind: string
}

interface Props {
  page_title: string
  artifacts: Artifact[]
  pagination: Pagination
  current_filters: CurrentFilters
  scope_worker?: ScopeWorker
  // Ficha completa do agente em agent-scope: alimenta o FichaHeader dentro do
  // WorkerPortalShell (a moldura do portal). Ausente no modo org-level.
  scope_worker_header?: DigitalWorkerDetail
}

// Anatomia de th do DS (Iteração 1): 11/650 uppercase baixa.
const TH_CLS = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-faint-foreground'

const KIND_OPTIONS = [
  { value: '', label: 'Todos os tipos' },
  { value: 'page', label: 'Página' },
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'report', label: 'Relatório' },
  { value: 'proposal', label: 'Proposta' },
  { value: 'status', label: 'Status' },
]

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'draft', label: 'Rascunho' },
  { value: 'published', label: 'Publicado' },
  { value: 'archived', label: 'Arquivado' },
]

function navigate(filters: CurrentFilters, page?: number, scopeWorker?: ScopeWorker) {
  // In agent scope the page lives on the canonical worker URL (C.2, SZ-1597);
  // filter/pagination visits stay there instead of round-tripping through the
  // legacy ?digital_worker_id= redirect.
  const basePath = scopeWorker
    ? `/dashboard/digital_workers/${scopeWorker.id}/publicacoes`
    : '/dashboard/artifacts'
  router.get(basePath, {
    page: page || undefined,
    search: filters.search || undefined,
    status: filters.status || undefined,
    kind: filters.kind || undefined,
  }, { preserveState: true })
}

export default function ArtifactsIndex({ page_title, artifacts, pagination, current_filters, scope_worker, scope_worker_header }: Props) {
  const { can_manage_resources } = usePermissions()
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const deleteTarget = artifacts.find((a) => a.id === deleteTargetId)
  const hasFilters = current_filters.search || current_filters.status || current_filters.kind

  const copyUrl = (artifact: Artifact) => {
    if (!artifact.public_url) return

    navigator.clipboard.writeText(artifact.public_url).then(() => {
      setCopiedId(artifact.id)
      setTimeout(() => setCopiedId(null), 2000)
    }).catch(() => {})
  }

  const onSearchChange = (value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      navigate({ ...current_filters, search: value }, undefined, scope_worker)
    }, 300)
  }

  const searchInputRef = useRef<HTMLInputElement>(null)

  // Limpa filtros e zera o input (não-controlado, via ref) numa tacada só.
  const clearFilters = () => {
    if (searchInputRef.current) searchInputRef.current.value = ''
    navigate({ search: '', status: '', kind: '' }, undefined, scope_worker)
  }

  const body = (
    <>
      <div>
        {/* Título de página do Painel: 19/650 (escala do DS). */}
        <h1 className="text-[19px] font-semibold tracking-[-0.01em] text-foreground">{page_title}</h1>
        <p className="text-[13px] text-muted-foreground mt-1">
          {scope_worker
            ? `Publicações criadas por ${scope_worker.name}.`
            : 'Publicações criadas pelos seus agentes.'}
        </p>
      </div>

      <div className="flex flex-col gap-2 md:flex-row">
        <div className="relative md:w-[16rem]">
          <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            placeholder="Buscar publicações…"
            defaultValue={current_filters.search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-8"
          />
        </div>
        <Select value={current_filters.kind || 'all'} onValueChange={(v) => navigate({ ...current_filters, kind: v === 'all' ? '' : v }, undefined, scope_worker)}>
          <SelectTrigger className="md:w-[10rem]" aria-label="Filtrar por tipo">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {KIND_OPTIONS.map((o) => <SelectItem key={o.value || 'all'} value={o.value || 'all'}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={current_filters.status || 'all'} onValueChange={(v) => navigate({ ...current_filters, status: v === 'all' ? '' : v }, undefined, scope_worker)}>
          <SelectTrigger className="md:w-[10rem]" aria-label="Filtrar por status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => <SelectItem key={o.value || 'all'} value={o.value || 'all'}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {artifacts.length === 0 ? (
        hasFilters ? (
          // Busca/filtro sem resultado (tipo B): spot central com eco do termo
          // e saída pra limpar — não é mais o beco cinza de antes.
          <FilterEmptyState query={current_filters.search} onClear={clearFilters} />
        ) : (
          <EmptyState
            illustration="publications"
            title="Nada publicado ainda"
            description={scope_worker
              ? `As publicações de ${scope_worker.name} aparecem aqui conforme o trabalho acontece.`
              : 'Seus agentes publicam aqui automaticamente durante as execuções.'}
          />
        )
      ) : (
      <div className="rounded-xl border border-border bg-card">
          <Table aria-label="Lista de publicações" className="text-[13px]">
            <TableHeader>
              <TableRow>
                <TableHead className={TH_CLS}>Título</TableHead>
                <TableHead className={`hidden md:table-cell ${TH_CLS}`}>Agente</TableHead>
                <TableHead className={`hidden md:table-cell ${TH_CLS}`}>Atualizado</TableHead>
                <TableHead className={`hidden sm:table-cell ${TH_CLS}`}>Status</TableHead>
                <TableHead className="text-right"><span className="sr-only">Ações</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {artifacts.map((artifact) => (
                <ArtifactRow
                  key={artifact.id}
                  artifact={artifact}
                  copiedId={copiedId}
                  canManage={can_manage_resources}
                  onCopy={copyUrl}
                  onDelete={setDeleteTargetId}
                />
              ))}
            </TableBody>
          </Table>

        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between border-t px-4 py-3">
            <span className="text-[13px] tabular-nums text-muted-foreground">
              Página {pagination.current_page} de {pagination.total_pages} ({pagination.total_count} publicações)
            </span>
            <div className="flex gap-2">
              {pagination.current_page > 1 && (
                <Button variant="outline" size="sm" onClick={() => navigate(current_filters, pagination.current_page - 1, scope_worker)}>
                  Anterior
                </Button>
              )}
              {pagination.current_page < pagination.total_pages && (
                <Button variant="outline" size="sm" onClick={() => navigate(current_filters, pagination.current_page + 1, scope_worker)}>
                  Próxima
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
      )}

      <AlertDialog open={deleteTargetId !== null} onOpenChange={(open) => { if (!open) setDeleteTargetId(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir publicação</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir &quot;{deleteTarget?.title}&quot;? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={() => {
                if (deleteTargetId) router.delete(`/dashboard/artifacts/${deleteTargetId}`)
                setDeleteTargetId(null)
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )

  return (
    <>
      <Head title={page_title} />
      {/* Em agent-scope a página vive dentro da moldura do portal (FichaHeader
          + WorkerPortalNav do WorkerPortalShell); no modo org-level segue seca
          num container simples — comportamento org-level intocado. */}
      {scope_worker_header ? (
        <WorkerPortalShell worker={scope_worker_header} section="publicacoes">{body}</WorkerPortalShell>
      ) : (
        <div className="space-y-6">{body}</div>
      )}
    </>
  )
}
